import React, { useState } from "react";
import { connect } from "react-redux";
import { Button, Rate } from "antd";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  DownOutlined,
  PlusOutlined,
  UpOutlined
} from "@ant-design/icons";

import AddFeedback from "./addFeedback";
import ExpandedFeedback from "./expandedFeedback";
import {
  buildGeneralSkillsMap,
  buildSkillCategorizedMap,
  buildEventRolesMap,
  transformFeedbacks
} from "./utils/feedbackAdapter";
import {
  getTalentNotes,
  updateFeedback,
  deleteFeedback
} from "store/actions/talents";
import { createSelector } from "reselect";
import { useNavigate } from "react-router-dom";
import { RoutingConstants } from "constants";

const getNotesData = (state) => state.talent.notesData;
const getGeneralSkillAttributes = (state) => state.talent?.generalSkillAttributes;
const getSkillCategorized = (state) => state.talent?.skillCategorized;
const getEventRoles = (state) => state.talent?.eventRoles;
const getCurrentUserUuid = (state) => state.auth.loginDetails?.person_uuid;

const selectFeedbacks = createSelector(
  [
    getNotesData,
    getGeneralSkillAttributes,
    getSkillCategorized,
    getEventRoles,
    getCurrentUserUuid
  ],
  (notesData, generalSkillAttributes, skillCategorized, eventRoles, currentUserUuid) => {
    const generalSkillsMap = buildGeneralSkillsMap(generalSkillAttributes);
    const skillsMap = buildSkillCategorizedMap(skillCategorized);
    const eventRolesMap = buildEventRolesMap(eventRoles);

    return transformFeedbacks(notesData, currentUserUuid, {
      generalSkillsMap,
      skillsMap,
      eventRolesMap
    });
  }
);

const SubmittedFeedbacks = ({
  feedbacks = [],
  personId,
  focusEventUuid,
  triggerUpdateFeedback,
  triggerDeleteFeedback,
  triggerTalentNotesList
}) => {
  const canCreateFeedback = feedbacks[0]?.canCreate ?? true;
  const navigate = useNavigate();

  const [expandedReview, setExpandedReview] = useState(null);
  // Deep link (?event_uuid=...) lands with Add Feedback open and that event preselected
  const [showFeedbackForm, setShowFeedbackForm] = useState(
    !!focusEventUuid || feedbacks.length === 0
  );
  const [linkedEventUuid, setLinkedEventUuid] = useState(focusEventUuid);
  const [editingReview, setEditingReview] = useState(null);

  const toggleAccordion = (noteId) => {
    setExpandedReview((prev) =>
      prev === noteId ? null : noteId
    );
  };

  const handleAddFeedback = () => {
    // Cancelling a deep-linked (?event_uuid=...) submission keeps the user in the feedback section
    // Both routes render the same TalentForm, so React keeps this state across the
    // URL change; reset it explicitly so the form closes with its fields cleared
    if (showFeedbackForm && focusEventUuid) {
      setShowFeedbackForm(false);
      setLinkedEventUuid(null);
      navigate(RoutingConstants.talents.viewFeedback.replace(":id", personId), { replace: true });
      return;
    }

    setShowFeedbackForm((prev) => !prev);
  };

  const handleFeedbackSuccess = () => {
    setShowFeedbackForm(false);
    setLinkedEventUuid(null);
  };

  const sortedFeedbacks = [...feedbacks].sort(
    (a, b) =>
      new Date(b.created_at) - new Date(a.created_at)
  );

  const handleEditReview = (noteId) => {
    setEditingReview(noteId);
  };

  const handleCancelEdit = () => {
    setEditingReview(null);
  };

  const handleSaveReview = (
    noteId,
    {
      ratings,
      additionalFeedback
    }
  ) => {
    const toRatingMap = (items = []) =>
      items.reduce((acc, item) => {
        acc[item.uuid] = item.rating ?? null;
        return acc;
      }, {});

    const actualPayload = {
      uuid: noteId,

      rating_stats: {
        general_skills: toRatingMap(
          ratings.general_skills
        ),

        event_roles: toRatingMap(
          ratings.event_roles
        ),

        skills: toRatingMap(
          ratings.skills
        )
      },

      desc: additionalFeedback
    };

    const payload = {
      actualPayload,

      onSuccess: () => {
        toast.success(
          "Performance feedback updated successfully."
        );

        triggerTalentNotesList({ id: personId });
        setEditingReview(null);
      },

      onError: (error) => {
        const errorMessage =
          error?.response?.data?.msg ||
          "Unable to update performance feedback. Please try again.";

        toast.error(
          typeof errorMessage === "string"
            ? errorMessage
            : "Unable to update performance feedback. Please try again."
        );
      }
    };

    triggerUpdateFeedback(payload);
  };

  const handleDeleteReview = (noteId) => {
    const payload = {
      actualPayload: { uuid: noteId },

      onSuccess: () => {
        toast.success(
          "Performance feedback deleted successfully."
        );

        setExpandedReview((prev) =>
          prev === noteId ? null : prev
        );

        triggerTalentNotesList({ id: personId });
      },

      onError: (error) => {
        const errorMessage =
          error?.response?.data?.msg ||
          "Unable to delete performance feedback. Please try again.";

        toast.error(
          typeof errorMessage === "string"
            ? errorMessage
            : "Unable to delete performance feedback. Please try again."
        );
      }
    };

    triggerDeleteFeedback(payload);
  };

  return (
    <div className="w-full">
      <div className="bg-white rounded-xl border border-grayBorder overflow-hidden shadow-sm">

        {/* Header */}
        <div className="px-4 py-5 border-b border-grayBorder flex items-center justify-between">
          <h2 className="text-lg font-semibold">
            Submitted Reviews
            <span className="text-gray-500 font-normal ml-2">
              ({sortedFeedbacks.length})
            </span>
          </h2>

          <div className="float-right gap-2 flex font-inter font-medium text-sm text-fontGray">
            <Button
              type="primary"
              className="flex items-center bg-secondary text-white hover:bg-secondaryDark hover:border-secondaryBorder active:bg-secondaryDark disabled:bg-btnDisableGray"
              onClick={handleAddFeedback}
              disabled={!showFeedbackForm && !canCreateFeedback}
              icon={!showFeedbackForm && <PlusOutlined className="align-baseline mt-three" />}
            >
              {showFeedbackForm ? "Cancel" : "Add Feedback"}
            </Button>
          </div>
        </div>

        {/* Add Feedback */}
        {showFeedbackForm && (
          <AddFeedback
            personId={personId}
            initialEventId={linkedEventUuid}
            onSuccess={handleFeedbackSuccess}
          />
        )}

        {/* Empty State */}
        {!sortedFeedbacks.length && !showFeedbackForm && (
          <div className="px-6 py-10 text-center">
            <div className="text-gray-900 font-medium">
              No reviews submitted yet
            </div>

            <p className="text-sm text-gray-500 mt-2">
              No performance feedback has been submitted for this
              person yet.
            </p>
          </div>
        )}

        {/* Reviews */}
        {sortedFeedbacks.map((feedback) => {
          const expanded =
            expandedReview === feedback.note_id;

          return (
            <div
              key={feedback.note_id}
              className="border-b border-grayBorder last:border-b-0"
            >
              <div
                onClick={() =>
                  toggleAccordion(feedback.note_id)
                }
                className="cursor-pointer hover:bg-gray-50 transition"
              >
                <div className="grid grid-cols-12 items-center p-4 gap-4">

                  <div className="col-span-5 flex items-start">
                    <div className="min-w-0 w-full">
                      <div
                        className="font-semibold text-gray-900 truncate"
                        title={feedback.event?.label || "--"}
                      >
                        {feedback.event?.label || "--"}
                      </div>
                      <div
                        className="text-sm text-gray-500 mt-1 truncate"
                        title={feedback.role && ` ${feedback.role} •  ${feedback.eventDate}`}
                      >
                        {feedback.role &&
                          ` ${feedback.role} • `}
                        {feedback.eventDate || "--"}
                      </div>
                    </div>
                  </div>

                  <div className="col-span-3 flex flex-col items-center justify-center">
                    <div className="font-semibold text-gray-900 leading-none mb-1">
                      {Number(feedback.rating || 0).toFixed(1)}
                    </div>

                    <Rate
                      allowHalf
                      disabled
                      value={feedback.rating || 0}
                      className="whitespace-nowrap"
                      style={{
                        fontSize: 15,
                        lineHeight: 1
                      }}
                    />
                  </div>

                  <div className="col-span-3 min-w-0 w-full">
                    <div className="font-medium text-gray-900 truncate text-xs"
                      title={feedback.author || "--"}
                    >
                      {feedback.author || "--"}
                    </div>

                    <div className="text-gray-500 mt-1 text-xs">
                      {feedback.created_at || "--"}
                    </div>
                  </div>

                  <div className="col-span-1 flex justify-end text-gray-500 text-lg">
                    {expanded ? (
                      <UpOutlined />
                    ) : (
                      <DownOutlined />
                    )}
                  </div>
                </div>
              </div>

              {expanded && (
                <ExpandedFeedback
                  feedback={feedback}
                  canEdit={feedback.canEdit}
                  canDelete={feedback.canDelete}
                  isEditing={editingReview === feedback.note_id}
                  onEdit={() =>
                    handleEditReview(feedback.note_id)
                  }
                  onCancel={handleCancelEdit}
                  onSave={(updatedRatings) =>
                    handleSaveReview(
                      feedback.note_id,
                      updatedRatings
                    )
                  }
                  onDelete={() =>
                    handleDeleteReview(feedback.note_id)
                  }
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  feedbacks: selectFeedbacks(state)
});

const mapDispatchToProps = {
  triggerUpdateFeedback: updateFeedback,
  triggerDeleteFeedback: deleteFeedback,
  triggerTalentNotesList: getTalentNotes
};

export default connect(mapStateToProps, mapDispatchToProps)(SubmittedFeedbacks);
